// @givmo/observability — PII-safe redaction utilities.
//
// The web parity for iOS's `LogScrubber` (givmo-ios Givmo/Core/Security/LogScrubber.swift):
// a deny-list scrubber that strips sensitive values from any string OR structured value
// before it can leave the browser. Two complementary layers:
//
//   1. `redactString` — regex deny-list over free text (JWTs, emails, phone numbers,
//      Bearer tokens, card-like numbers, money amounts, an AI donation link's `gco_`
//      token, a link's `token=` value and a guest pledge link's token in its backend path).
//      Mirrors LogScrubber's patterns and adds the web-specific money/card threats called
//      out in 03 §2.
//   2. `redactValue` / `redactObject` — walk a structured value and (a) drop the VALUE of
//      any key whose name matches the sensitive-key deny-list (email/amount/token/etc.),
//      and (b) run `redactString` over every remaining string leaf.
//
// This module is framework-agnostic and has no Sentry dependency — it is the reusable
// primitive that `scrub.ts` applies to Sentry events + breadcrumbs, and that consuming
// apps can reuse for their own logging. The invariant it guarantees: no email, amount,
// token, or other PII survives a redaction pass, in a string or nested anywhere in an
// object/array.

/** Replacement token emitted in place of a redacted value. */
export const REDACTED = '[REDACTED]' as const;

// MARK: - String patterns (deny-list)
//
// Ordering matters: more specific / longer patterns run before broader ones so a JWT or a
// "Bearer <token>" is fully masked before the email/number patterns get a chance to nibble
// at fragments of it. All patterns are global + case-insensitive where relevant.

/** JWT: three base64url segments (header.payload.signature). Matches LogScrubber. */
const JWT = /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g;

/** Authorization-style Bearer token. Matches LogScrubber. */
const BEARER = /Bearer\s+[A-Za-z0-9._~+/=-]+/gi;

/**
 * An AI donation link's single-use checkout token (MCP-A29f): `gco_` and URL-safe base64, the
 * backend's `CHECKOUT_TOKEN_PREFIX` + `token_urlsafe(32)`. It is a bearer for one gift in the
 * account holder's name, and the donate site's `/give#gco_…` puts it in the page's FIRST address,
 * which Sentry's navigation breadcrumbs record (with the fragment) when the page takes it out of
 * the address bar, and again when a second link opens in the same tab. Masked before the
 * number patterns can nibble at digit runs inside it.
 */
const CHECKOUT_TOKEN = /gco_[A-Za-z0-9_-]+/g;

/**
 * A link's bearer in an address: the value of any query (or fragment) parameter named for a token
 * (`token=`, `invite_token=`, `access_token=`). The donate site's guest pledge link,
 * `/pledges/cancel?token=…`, is one (it reads, lowers and cancels a guest's pledge); the token is
 * opaque URL-safe base64, which no other pattern recognises. Its name is kept, its value masked.
 */
const LINK_TOKEN = /(^|[?&#\s])([\w.-]*token=)[^&#\s"'<>]+/gi;

/**
 * The same guest link's token in the backend paths the page calls with it
 * (`/public/pledges/claim/{token}`, `…/amount`, `…/cancel`), which fetch breadcrumbs record.
 */
const PLEDGE_CLAIM_PATH = /(\/pledges\/claim\/)[^/?#\s"'<>]+/gi;

/**
 * An organizer message's unsubscribe token in the backend path the donate site's unsubscribe page calls
 * with it (`/public/organizer-messages/unsubscribe/{token}`, FCM-WEB52): one delivery's bearer, which
 * reads and applies its stops, and which fetch breadcrumbs record. Its page's own address carries it as
 * `token=`, which `LINK_TOKEN` masks.
 */
const ORGANIZER_UNSUBSCRIBE_PATH = /(\/organizer-messages\/unsubscribe\/)[^/?#\s"'<>]+/gi;

/** Email addresses. Matches LogScrubber. */
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/**
 * Card-like number runs: 13–19 digits, optionally separated by single spaces or hyphens
 * (covers PANs the way they're typed/pasted). Web-specific — iOS never handles raw PANs,
 * but a misbehaving Shift4/wallet callback could surface one here.
 */
const CARD = /\b(?:\d[ -]?){12,18}\d\b/g;

/** US phone numbers: +1XXXXXXXXXX, (XXX) XXX-XXXX, XXX-XXX-XXXX, etc. Matches LogScrubber. */
const PHONE = /(?:\+1[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g;

/**
 * Money amounts: a currency-prefixed number (`$1,234.56`, `USD 1234.56`) OR a labelled
 * amount (`amount: 1234`, `amount=12.50`). Donation amounts are PII under the 03 §2
 * "no amount in events OR breadcrumbs" invariant.
 */
const CURRENCY_AMOUNT = /(?:\$|USD\s?)\s?\d{1,3}(?:[,\s]?\d{3})*(?:\.\d{1,2})?/gi;
const LABELLED_AMOUNT = /\bamount["']?\s*[:=]\s*"?\$?\d[\d,]*(?:\.\d{1,2})?"?/gi;

/**
 * Redact sensitive substrings from free text. Idempotent — running it on already-redacted
 * text is a no-op for the masked spans. Non-strings are returned unchanged.
 */
export function redactString(input: string): string {
  if (input.length === 0) return input;
  let out = input;
  out = out.replace(JWT, REDACTED);
  out = out.replace(BEARER, `Bearer ${REDACTED}`);
  out = out.replace(CHECKOUT_TOKEN, REDACTED);
  out = out.replace(LINK_TOKEN, `$1$2${REDACTED}`);
  out = out.replace(PLEDGE_CLAIM_PATH, `$1${REDACTED}`);
  out = out.replace(ORGANIZER_UNSUBSCRIBE_PATH, `$1${REDACTED}`);
  out = out.replace(EMAIL, REDACTED);
  out = out.replace(CARD, REDACTED);
  out = out.replace(LABELLED_AMOUNT, `amount: ${REDACTED}`);
  out = out.replace(CURRENCY_AMOUNT, REDACTED);
  out = out.replace(PHONE, REDACTED);
  return out;
}

// MARK: - Sensitive key deny-list (structured redaction)
//
// Some PII never reaches a string the regexes can catch — it sits in a typed field on an
// object (`{ email, amount, token }`). For structured values we drop the VALUE of any key
// whose name matches one of these fragments (case-insensitive substring match), regardless
// of the value's type. This is the "deny-list" half of the 03 §2 posture.

const SENSITIVE_KEY_FRAGMENTS = [
  'email',
  'phone',
  'amount',
  'token',
  'password',
  'secret',
  'authorization',
  'auth',
  'cookie',
  'creditcard',
  'cardnumber',
  'cvv',
  'cvc',
  'ssn',
  'apikey',
  'accesstoken',
  'refreshtoken',
  'idtoken',
  'firstname',
  'lastname',
  'fullname',
  'address',
  // Bank-payment fields (the charity payout surface — the product's most sensitive data). The
  // normalized key strips `_`/`-`/case, so these fragments catch `account_number`, `routing_number`,
  // `accountNumber`, `routingNumber`, `bank_account`, `bank_name`, `iban`, etc. Over-redacting a
  // telemetry key is safe (it only affects Sentry scrubbing, never app logic), so this is pure
  // defense-in-depth for a value that must never appear in an event.
  'account',
  'routing',
  'bank',
  'iban',
] as const;

/** True when `key` (normalized) contains a sensitive fragment. */
export function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[_\-\s]/g, '');
  return SENSITIVE_KEY_FRAGMENTS.some((fragment) => normalized.includes(fragment));
}

/**
 * Deep-redact an arbitrary value:
 *  - strings → `redactString`
 *  - arrays → each element redacted
 *  - plain objects → sensitive-keyed entries replaced with `[REDACTED]`; every other entry
 *    redacted recursively
 *  - everything else (number/boolean/null/undefined) → returned unchanged
 *
 * Cyclic references are handled with a `WeakSet` guard so a self-referential context object
 * can never spin the scrubber. The input is never mutated — a redacted copy is returned.
 */
export function redactValue(value: unknown): unknown {
  return redactInternal(value, new WeakSet<object>());
}

function redactInternal(value: unknown, seen: WeakSet<object>): unknown {
  if (typeof value === 'string') {
    return redactString(value);
  }
  if (Array.isArray(value)) {
    if (seen.has(value)) return REDACTED;
    seen.add(value);
    return value.map((item) => redactInternal(item, seen));
  }
  if (value !== null && typeof value === 'object') {
    if (seen.has(value)) return REDACTED;
    seen.add(value);
    const source = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(source)) {
      result[key] = isSensitiveKey(key) ? REDACTED : redactInternal(source[key], seen);
    }
    return result;
  }
  return value;
}

/**
 * Convenience wrapper for object inputs: redacts in place semantics are NOT used — a
 * redacted shallow-typed copy is returned. Returns `undefined` unchanged so callers can
 * map optional fields without a guard.
 */
export function redactObject<T extends Record<string, unknown> | undefined>(
  input: T,
): T extends undefined ? undefined : Record<string, unknown> {
  if (input === undefined) {
    return undefined as T extends undefined ? undefined : Record<string, unknown>;
  }
  return redactValue(input) as T extends undefined ? undefined : Record<string, unknown>;
}
